import {
  BLACK,
  WHITE,
  colorOfSlot,
  currentGame,
  generateLegalMoves,
  isCapture,
  isGameOver,
  matchIntent,
  moveToUci,
  opposite,
  positionAtPly,
  serializePgn,
  type Color,
  type MoveIntent,
  type PlayerSlot,
  type PromotionPiece,
  type Square,
} from '@chess/core'
import { useCallback, useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react'
import { Board } from '../board/Board.js'
import { layoutAtPly } from '../board/layout.js'
import { vibrate } from '../lib/haptics.js'
import { useWakeLock } from '../lib/useWakeLock.js'
import { playSound, unlockAudio, type SoundName } from '../sound/engine.js'
import {
  Banner,
  ConnectionPill,
  FloatingReactions,
  GameOverCard,
  PromotionPicker,
  Toast,
} from './Overlays.js'
import { PlayerBar } from './PlayerBar.js'
import { SidePanel } from './SidePanel.js'
import type { RoomConnection } from './connection.js'

export interface RoomViewProps {
  readonly connection: RoomConnection
  /** Null when watching rather than playing. */
  readonly mySlot: PlayerSlot | null
  readonly header?: ReactNode
  readonly notice?: ReactNode
}

interface PendingPromotion {
  readonly from: Square
  readonly to: Square
  readonly queued: boolean
}

export const RoomView = ({ connection, mySlot, header, notice }: RoomViewProps): JSX.Element => {
  const { state: room, send, opponentOnline, status, lastError, clearError } = connection
  const slot = currentGame(room)
  const { game } = slot
  const over = isGameOver(game.status)

  const [seekPly, setSeekPly] = useState<number | null>(null)
  const [flipped, setFlipped] = useState(false)
  const [premove, setPremove] = useState<MoveIntent | null>(null)
  const [promotion, setPromotion] = useState<PendingPromotion | null>(null)
  const [dismissedGame, setDismissedGame] = useState<number | null>(null)
  const [readChatCount, setReadChatCount] = useState(room.chat.length)
  const [toast, setToast] = useState<string | null>(null)
  const [flipping, setFlipping] = useState(false)

  const livePly = game.history.length
  const ply = seekPly ?? livePly
  const reviewing = ply !== livePly

  const myColor: Color | null = mySlot === null ? null : colorOfSlot(mySlot, slot.index)
  const orientation = useMemo<Color>(() => {
    const base = myColor ?? WHITE
    return flipped ? opposite(base) : base
  }, [flipped, myColor])

  const bothHere = room.players[0].arrived && room.players[1].arrived
  const myTurn = myColor !== null && !over && bothHere && game.position.turn === myColor

  // Keep the screen awake while a game is actually running. Nothing is more
  // annoying than the display locking while you think.
  useWakeLock(!over && bothHere)

  const position = useMemo(() => positionAtPly(game, ply), [game, ply])
  const layout = useMemo(() => layoutAtPly(game, ply), [game, ply])

  const legalMoves = useMemo(
    () => (reviewing || !myTurn ? [] : generateLegalMoves(game.position)),
    [game.position, myTurn, reviewing],
  )

  // Moves that may be queued while the opponent thinks. Generated as though it
  // were already our turn, which is an approximation — the opponent's reply may
  // invalidate it, and that is exactly why a premove can fail and be dropped.
  const premoveMoves = useMemo(
    () =>
      reviewing || myTurn || over || myColor === null || !bothHere
        ? []
        : generateLegalMoves({ ...game.position, turn: myColor }),
    [bothHere, game.position, myColor, myTurn, over, reviewing],
  )

  const lastEntry = ply > 0 ? (game.history[ply - 1] ?? null) : null
  const checkSquare =
    !reviewing && game.status.kind === 'playing' && game.status.check
      ? game.position.kings[game.position.turn]
      : null

  /* ── sound and feel ───────────────────────────────────────────────────── */

  const seenPly = useRef(livePly)
  const seenStatus = useRef(game.status.kind)
  const seenGameCount = useRef(room.games.length)

  useEffect(() => {
    if (room.games.length !== seenGameCount.current) {
      seenGameCount.current = room.games.length
      seenPly.current = 0
      seenStatus.current = 'playing'
      setSeekPly(null)
      setPremove(null)
      setDismissedGame(null)
      playSound('gameStart')
      return
    }

    if (livePly > seenPly.current) {
      const entry = game.history[livePly - 1]
      if (entry !== undefined) {
        const mover = opposite(game.position.turn)
        const mine = mover === myColor
        const capture = isCapture(entry.move)
        playSound(soundFor(entry.san, entry.move.kind, capture, mine))
        if (mine) vibrate(capture ? 'capture' : 'move')
      }
      seenPly.current = livePly
      setSeekPly(null)
    }

    if (game.status.kind !== seenStatus.current) {
      if (isGameOver(game.status)) {
        playSound('gameEnd')
        vibrate('end')
      } else if (game.status.kind === 'playing' && game.status.check) {
        playSound('check')
      }
      seenStatus.current = game.status.kind
    }
  }, [game, livePly, myColor, room.games.length])

  /** Only warn on your own clock — the opponent's is their problem. */
  const handleUrgent = useCallback(
    (color: Color): void => {
      if (color === myColor) playSound('lowTime')
    },
    [myColor],
  )

  /* ── errors the server sent back ──────────────────────────────────────── */

  useEffect(() => {
    if (lastError === null) return
    playSound('illegal')
    vibrate('illegal')
    setToast(lastError)
    const id = window.setTimeout(clearError, 2_600)
    return () => window.clearTimeout(id)
  }, [lastError, clearError])

  useEffect(() => {
    if (toast === null) return
    const id = window.setTimeout(() => setToast(null), 2_600)
    return () => window.clearTimeout(id)
  }, [toast])

  /* ── premove ──────────────────────────────────────────────────────────── */

  useEffect(() => {
    if (premove === null) return
    if (!myTurn) return

    const move = matchIntent(generateLegalMoves(game.position), premove)
    setPremove(null)
    if (move === null) {
      playSound('illegal')
      vibrate('illegal')
      return
    }
    send({ type: 'move', uci: moveToUci(move) })
  }, [game.position, myTurn, premove, send])

  /* ── input ────────────────────────────────────────────────────────────── */

  const submitMove = useCallback(
    (intent: MoveIntent): void => {
      unlockAudio()
      const move = matchIntent(generateLegalMoves(game.position), intent)
      if (move === null) {
        playSound('illegal')
        vibrate('illegal')
        return
      }
      send({ type: 'move', uci: moveToUci(move) })
    },
    [game.position, send],
  )

  const handleIllegal = useCallback((): void => {
    playSound('illegal')
    vibrate('illegal')
  }, [])

  const handlePickUp = useCallback((): void => {
    unlockAudio()
    vibrate('select')
  }, [])

  const handlePromotion = useCallback((from: Square, to: Square, queued: boolean): void => {
    setPromotion({ from, to, queued })
  }, [])

  const choosePromotion = useCallback(
    (piece: PromotionPiece): void => {
      if (promotion === null) return
      const intent: MoveIntent = { from: promotion.from, to: promotion.to, promoteTo: piece }
      setPromotion(null)
      if (promotion.queued) {
        setPremove(intent)
        playSound('premove')
      } else {
        submitMove(intent)
      }
    },
    [promotion, submitMove],
  )

  const queuePremove = useCallback((intent: MoveIntent | null): void => {
    setPremove(intent)
    if (intent !== null) {
      playSound('premove')
      vibrate('select')
    }
  }, [])

  const flip = useCallback((): void => {
    setFlipped((value) => !value)
    setFlipping(true)
    window.setTimeout(() => setFlipping(false), 260)
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.target instanceof HTMLInputElement) return
      if (event.key === 'Escape') {
        setPromotion(null)
        return
      }
      if (event.key === 'ArrowLeft') setSeekPly((current) => Math.max(0, (current ?? livePly) - 1))
      else if (event.key === 'ArrowRight')
        setSeekPly((current) => Math.min(livePly, (current ?? livePly) + 1))
      else if (event.key === 'ArrowUp') setSeekPly(0)
      else if (event.key === 'ArrowDown') setSeekPly(null)
      else if (event.key === 'f') flip()
      else return
      event.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [flip, livePly])

  const copyPgn = useCallback((): void => {
    const text = serializePgn(game, {
      Event: 'Casual game',
      White: room.players[colorOfSlot(0, slot.index) === WHITE ? 0 : 1].name,
      Black: room.players[colorOfSlot(0, slot.index) === WHITE ? 1 : 0].name,
    })
    void navigator.clipboard?.writeText(text).then(
      () => setToast('PGN copied'),
      () => setToast('Could not copy'),
    )
  }, [game, room.players, slot.index])

  /* ── layout ───────────────────────────────────────────────────────────── */

  const topColor = opposite(orientation)
  // A bot holds no socket, so socket presence would show it as away forever.
  const opponentPresent = room.opponent.kind === 'ai' ? true : opponentOnline
  const botThinking =
    room.opponent.kind === 'ai' && !over && bothHere && game.position.turn !== myColor

  const seatOf = (color: Color): PlayerSlot => (colorOfSlot(0, slot.index) === color ? 0 : 1)

  const bars = {
    [WHITE]: {
      name: room.players[seatOf(WHITE)].name,
      avatar: room.players[seatOf(WHITE)].avatar,
      online: mySlot !== null && colorOfSlot(mySlot, slot.index) === WHITE ? true : opponentPresent,
    },
    [BLACK]: {
      name: room.players[seatOf(BLACK)].name,
      avatar: room.players[seatOf(BLACK)].avatar,
      online: mySlot !== null && colorOfSlot(mySlot, slot.index) === BLACK ? true : opponentPresent,
    },
  } as const

  const showGameOver = over && dismissedGame !== slot.index

  const barFor = (color: Color): JSX.Element => (
    <PlayerBar
      name={bars[color].name}
      avatar={bars[color].avatar}
      color={color}
      position={position}
      clock={slot.clock}
      turn={game.position.turn}
      live={!over}
      offsetMs={connection.nowOffsetMs}
      online={bars[color].online}
      showClock={room.timeControl.kind === 'clock'}
      thinking={botThinking && color !== myColor}
      onUrgent={() => handleUrgent(color)}
    />
  )

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 lg:flex-row lg:items-start lg:p-4 landscape:max-lg:flex-row landscape:max-lg:gap-2 landscape:max-lg:p-2">
      {/* The board is square, so its width is bounded by the height left over
          after the two player bars and the header. In landscape on a phone the
          binding constraint is the viewport height instead. */}
      <div className="flex w-full min-w-0 flex-col lg:max-w-[min(100%,calc(100vh-11rem))] landscape:max-lg:max-w-[min(58vw,calc(100dvh-7.75rem))]">
        <div className="landscape:max-lg:hidden">{header}</div>

        <div className="px-2 sm:px-0">{barFor(topColor)}</div>

        <div className={`relative ${flipping ? 'board-flip' : ''}`}>
          <Board
            layout={layout}
            orientation={orientation}
            legalMoves={legalMoves}
            premoveMoves={premoveMoves}
            lastMove={lastEntry?.move ?? null}
            checkSquare={checkSquare}
            premove={premove}
            onMove={submitMove}
            onPremove={queuePremove}
            onPromotion={handlePromotion}
            onIllegal={handleIllegal}
            onPickUp={handlePickUp}
            animate={!reviewing}
            ply={ply}
          />

          <FloatingReactions reactions={room.reactions} />

          {promotion !== null && (
            <PromotionPicker
              to={promotion.to}
              color={myColor ?? game.position.turn}
              orientation={orientation}
              onChoose={choosePromotion}
              onCancel={() => setPromotion(null)}
            />
          )}

          {showGameOver && (
            <GameOverCard
              status={game.status}
              myColor={myColor}
              names={[bars[WHITE].name, bars[BLACK].name]}
              rematchPending={room.rematchOfferedBy !== null && room.rematchOfferedBy !== mySlot}
              awaitingOpponent={room.rematchOfferedBy !== null && room.rematchOfferedBy === mySlot}
              canRematch={mySlot !== null}
              onRematch={() => send({ type: 'offerRematch' })}
              onDismiss={() => setDismissedGame(slot.index)}
            />
          )}

          <ConnectionPill status={status} />
          {toast !== null && <Toast>{toast}</Toast>}
        </div>

        <div className="px-2 sm:px-0">{barFor(orientation)}</div>

        <div className="space-y-2 px-2 sm:px-0">
          {notice}
          {!bothHere && mySlot !== null && <Banner>Waiting for your opponent to open the link…</Banner>}
          {reviewing && (
            <button
              type="button"
              onClick={() => setSeekPly(null)}
              className="w-full rounded bg-accent px-3 py-2 text-sm font-bold text-black"
            >
              Back to the live position
            </button>
          )}
        </div>
      </div>

      <div className="flex min-h-0 w-full flex-col px-2 pb-4 safe-bottom sm:px-0 lg:w-80 lg:px-0 landscape:max-lg:w-auto landscape:max-lg:flex-1 landscape:max-lg:px-0 landscape:max-lg:pb-0">
        <SidePanel
          room={room}
          game={game}
          ply={ply}
          onSeek={(target) => setSeekPly(target === livePly ? null : target)}
          send={send}
          mySlot={mySlot}
          canAct={mySlot !== null}
          onFlip={flip}
          onCopyPgn={copyPgn}
          unreadChat={Math.max(0, room.chat.length - readChatCount)}
          onReadChat={() => setReadChatCount(room.chat.length)}
        />
      </div>
    </div>
  )
}

const soundFor = (san: string, kind: string, capture: boolean, mine: boolean): SoundName => {
  if (san.endsWith('#')) return 'gameEnd'
  if (kind === 'castle') return 'castle'
  if (kind === 'promotion') return 'promote'
  if (san.endsWith('+')) return 'check'
  if (capture) return 'capture'
  return mine ? 'moveSelf' : 'moveOpponent'
}
